import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  useTheme,
  InputAdornment
} from '@mui/material';
import {
  Search,
  FilterList,
  GetApp,
  Refresh,
  Add,
  Clear
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

import ConsultationService from '../services/consultationService';
import { 
  CONSULTATION_OPTIONS, 
  getStatusColor, 
  getStatusLabel,
  DEFAULT_CONSULTATION 
} from '../config/consultationConfig';

const ConsultationDashboard = () => {
  // State management
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalConsultations, setTotalConsultations] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    quality: '',
    status: '',
    membership: '',
    campaign: '',
    dateFrom: null,
    dateTo: null
  });

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [updatingRows, setUpdatingRows] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  
  // Cell refs for editing
  const cellRefs = useRef({});
  
  // Theme
  const theme = useTheme();

  // Load consultations
  const loadConsultations = useCallback(async () => {
    try {
      setLoading(true);
      const options = {
        ...filters,
        sortField: 'date',
        sortOrder: { ascending: false }
      };
      
      const data = await ConsultationService.getConsultations(options);
      setConsultations(data);
      setTotalConsultations(data.length);
    } catch (error) {
      console.error('Error loading consultations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load consultations: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const stats = await ConsultationService.getConsultationStats();
      setStats(stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadConsultations();
    loadStats();
  }, [loadConsultations, loadStats]);

  // Handle cell edit
  const processRowUpdate = async (newRow, oldRow) => {
    try {
      // Find what changed
      const changedField = Object.keys(newRow).find(key => newRow[key] !== oldRow[key]);
      
      if (changedField) {
        // Add row to updating state
        setUpdatingRows(prev => new Set(prev).add(newRow.id));
        
        // Update database
        await ConsultationService.updateConsultation(newRow.id, { [changedField]: newRow[changedField] });
        
        // Update local state to ensure UI reflects the change immediately
        setConsultations(prev => prev.map(consultation => 
          consultation.id === newRow.id 
            ? { ...consultation, [changedField]: newRow[changedField] }
            : consultation
        ));
        
        setSnackbar({
          open: true,
          message: `${changedField.replace('_', ' ').charAt(0).toUpperCase() + changedField.replace('_', ' ').slice(1)} updated successfully`,
          severity: 'success'
        });

        // Reload stats
        loadStats();
      }
      
      // Remove row from updating state
      setUpdatingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(newRow.id);
        return newSet;
      });
      
      return newRow;
    } catch (error) {
      console.error('Error updating consultation:', error);
      
      // Remove row from updating state
      setUpdatingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(newRow.id);
        return newSet;
      });
      
      setSnackbar({
        open: true,
        message: 'Failed to update: ' + error.message,
        severity: 'error'
      });
      
      // Return old row to revert changes
      return oldRow;
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page
  };

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      quality: '',
      status: '',
      membership: '',
      campaign: '',
      dateFrom: null,
      dateTo: null
    });
  };

  // Export data
  const handleExport = async () => {
    try {
      setLoading(true);
      const { csv, filename } = await ConsultationService.exportConsultations(filters);
      
      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSnackbar({
        open: true,
        message: 'Export successful',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting:', error);
      setSnackbar({
        open: true,
        message: 'Export failed: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Bulk update
  const handleBulkUpdate = async (field, value) => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select rows to update',
        severity: 'warning'
      });
      return;
    }

    try {
      setBulkUpdating(true);
      await ConsultationService.bulkUpdateConsultations(selectedRows, { [field]: value });
      setSnackbar({
        open: true,
        message: `Updated ${selectedRows.length} records successfully`,
        severity: 'success'
      });
      loadConsultations();
      loadStats();
      setSelectedRows([]);
    } catch (error) {
      console.error('Error bulk updating:', error);
      setSnackbar({
        open: true,
        message: 'Bulk update failed: ' + error.message,
        severity: 'error'
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  // Handle cell click for single-click editing
  const handleCellClick = (params, event) => {
    // Do nothing - let our custom renderers handle the editing
    return;
  };

  // Handle edit cell change
  const handleCellEditStart = (params) => {
    setEditingCell({ id: params.id, field: params.field });
  };

  const handleCellEditStop = () => {
    setEditingCell(null);
  };

  // Custom cell renderer for status fields with color coding
  const renderStatusCell = (params) => {
    const { field, value, row } = params;
    const color = getStatusColor(field, value);
    const label = getStatusLabel(field, value);
    const isUpdating = updatingRows.has(row.id);
    const isEditing = editingCell?.id === row.id && editingCell?.field === field;
    
    const handleEditClick = (event) => {
      event.stopPropagation();
      setEditingCell({ id: row.id, field });
      
      // Find the cell element and trigger double-click to start editing
      setTimeout(() => {
        const cellElement = document.querySelector(`[data-id="${row.id}"] [data-field="${field}"]`);
        if (cellElement) {
          // Create and dispatch a double-click event
          const doubleClickEvent = new MouseEvent('dblclick', {
            bubbles: true,
            cancelable: true,
            view: window
          });
          cellElement.dispatchEvent(doubleClickEvent);
        }
      }, 50);
    };
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        width: '100%',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          cursor: 'pointer'
        }
      }}>
        {value ? (
          <Chip
            label={label}
            color={color}
            size="small"
            variant={field === 'membership' && value ? 'filled' : 'outlined'}
            onClick={handleEditClick}
            sx={{
              transition: 'all 0.3s ease-in-out',
              opacity: isUpdating ? 0.7 : 1,
              transform: isUpdating ? 'scale(0.95)' : 'scale(1)',
              cursor: 'pointer',
              flex: 1,
              maxWidth: '100%',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              ...(field === 'membership' && value && {
                fontWeight: 'bold',
                color: 'white !important',
                '& .MuiChip-label': {
                  color: 'white !important'
                }
              })
            }}
          />
        ) : (
          <Typography 
            variant="body2" 
            onClick={handleEditClick}
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              opacity: isUpdating ? 0.7 : 1,
              transition: 'opacity 0.3s ease-in-out',
              cursor: 'pointer',
              flex: 1
            }}
          >
            Click to select...
          </Typography>
        )}
        
        {isUpdating && (
          <CircularProgress 
            size={12} 
            sx={{ 
              color: 'action.active',
              animation: 'spin 1s linear infinite'
            }} 
          />
        )}
        {!isUpdating && !isEditing && (
          <Box 
            onClick={handleEditClick}
            sx={{ 
              width: 16, 
              height: 16, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              opacity: 0.5,
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              borderRadius: '2px',
              flexShrink: 0,
              '&:hover': { 
                opacity: 1,
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                transform: 'scale(1.1)'
              }
            }}
          >
            🔽
          </Box>
        )}
      </Box>
    );
  };

  // Custom cell renderer for text fields with loading indicator
  const renderTextCell = (params) => {
    const { value, row, field } = params;
    const isUpdating = updatingRows.has(row.id);
    const isEditing = editingCell?.id === row.id && editingCell?.field === params.field;
    
    const handleEditClick = (event) => {
      event.stopPropagation();
      setEditingCell({ id: row.id, field });
      
      // Find the cell element and trigger double-click to start editing
      setTimeout(() => {
        const cellElement = document.querySelector(`[data-id="${row.id}"] [data-field="${field}"]`);
        if (cellElement) {
          // Create and dispatch a double-click event
          const doubleClickEvent = new MouseEvent('dblclick', {
            bubbles: true,
            cancelable: true,
            view: window
          });
          cellElement.dispatchEvent(doubleClickEvent);
        }
      }, 50);
    };
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        width: '100%',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          cursor: 'pointer'
        }
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            flex: 1,
            transition: 'opacity 0.3s ease-in-out',
            opacity: isUpdating ? 0.7 : 1,
            color: value ? 'text.primary' : 'text.secondary',
            fontStyle: !value ? 'italic' : 'normal',
            cursor: 'pointer'
          }}
          onClick={handleEditClick}
        >
          {value || 'Click to add followup...'}
        </Typography>
        {isUpdating && (
          <CircularProgress 
            size={12} 
            sx={{ 
              color: 'action.active',
              animation: 'spin 1s linear infinite'
            }} 
          />
        )}
        {!isUpdating && !isEditing && (
          <Box 
            onClick={handleEditClick}
            sx={{ 
              width: 16, 
              height: 16, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              opacity: 0.5,
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              borderRadius: '2px',
              '&:hover': { 
                opacity: 1,
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                transform: 'scale(1.1)'
              }
            }}
          >
            ✏️
          </Box>
        )}
      </Box>
    );
  };

  // Column definitions
  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'MMM dd, yyyy');
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 120,
      editable: false
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 120,
      editable: false
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      editable: false,
      renderCell: (params) => (
        <a href={`tel:${params.value}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {params.value}
        </a>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      editable: false,
      renderCell: (params) => (
        <a href={`mailto:${params.value}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {params.value}
        </a>
      )
    },
    {
      field: 'quality',
      headerName: 'Quality',
      width: 140,
      editable: true,
      type: 'singleSelect',
      valueOptions: CONSULTATION_OPTIONS.quality.map(option => option.value),
      renderCell: renderStatusCell
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: CONSULTATION_OPTIONS.status.map(option => option.value),
      renderCell: renderStatusCell
    },
    {
      field: 'membership',
      headerName: 'Membership',
      width: 140,
      editable: true,
      type: 'singleSelect',
      valueOptions: CONSULTATION_OPTIONS.membership.map(option => option.value),
      renderCell: renderStatusCell
    },
    {
      field: 'followup',
      headerName: 'Followup',
      minWidth: 250,
      editable: true,
      type: 'string',
      sortable: true,
      renderCell: renderTextCell,
      flex: 1
    },
    {
      field: 'which_campaign',
      headerName: 'Campaign',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: CONSULTATION_OPTIONS.campaign.map(option => option.value),
      renderCell: renderStatusCell
    }
  ];

  // Get paginated data
  const paginatedConsultations = consultations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        height: '100vh', 
        width: '100%',
        bgcolor: theme.palette.background.default,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f5f5f5',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#555',
          borderRadius: '2px',
          '&:hover': {
            backgroundColor: '#333'
          }
        },
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            width: '100%', 
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: theme.palette.background.paper,
            borderRadius: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Concierge Medicine Submissions
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  color: theme.palette.text.secondary 
                }}
              >
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              placeholder="Search submissions..."
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              sx={{ 
                mr: 2, 
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  bgcolor: theme.palette.background.paper,
                  borderColor: theme.palette.divider,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={loadConsultations}
                sx={{ 
                  bgcolor: theme.palette.background.paper, 
                  borderRadius: '50%',
                  p: 1,
                  mr: 1,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={handleExport}
              disabled={loading}
              sx={{ 
                borderRadius: 4,
                bgcolor: theme.palette.background.paper,
                '&:hover': {
                  bgcolor: theme.palette.action.hover
                }
              }}
            >
              Export CSV
            </Button>
          </Box>
        </Paper>

        {/* Stats Cards */}
        <Box sx={{ px: 3, mb: 3 }}>
          {stats && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Submissions
                    </Typography>
                    <Typography variant="h5">
                      {stats.total}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Qualified
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {stats.byQuality['Qualified'] || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Contacted
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {stats.byStatus['Contacted'] || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Not Interested
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      {stats.byStatus['Not Interested'] || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ px: 3, mb: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Quality</InputLabel>
                  <Select
                    value={filters.quality}
                    label="Quality"
                    onChange={(e) => handleFilterChange('quality', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {CONSULTATION_OPTIONS.quality.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {CONSULTATION_OPTIONS.status.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Membership</InputLabel>
                  <Select
                    value={filters.membership}
                    label="Membership"
                    onChange={(e) => handleFilterChange('membership', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {CONSULTATION_OPTIONS.membership.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={filters.campaign}
                    label="Campaign"
                    onChange={(e) => handleFilterChange('campaign', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {CONSULTATION_OPTIONS.campaign.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <DatePicker
                  label="From Date"
                  value={filters.dateFrom}
                  onChange={(date) => handleFilterChange('dateFrom', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <DatePicker
                  label="To Date"
                  value={filters.dateTo}
                  onChange={(date) => handleFilterChange('dateTo', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Clear Filters">
                    <IconButton onClick={clearFilters}>
                      <Clear />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Action Buttons */}
        {selectedRows.length > 0 && (
          <Box sx={{ px: 3, mb: 2 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => handleBulkUpdate('status', 'Contacted')}
                disabled={bulkUpdating}
                startIcon={bulkUpdating ? <CircularProgress size={16} /> : null}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
              >
                {bulkUpdating ? 'Updating...' : `Mark ${selectedRows.length} as Contacted`}
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleBulkUpdate('status', 'Not Interested')}
                disabled={bulkUpdating}
                startIcon={bulkUpdating ? <CircularProgress size={16} /> : null}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
              >
                {bulkUpdating ? 'Updating...' : `Mark ${selectedRows.length} as Not Interested`}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Data Grid */}
        <Box sx={{ px: 3, mb: 2 }}>
          <Paper sx={{ 
            width: '100%',
            overflowX: 'auto', // Allow horizontal scrolling
            overflowY: 'visible', // Prevent vertical scrollbar competition
            '& .MuiDataGrid-root': {
              minWidth: 'max-content',
              width: '100%'
            },
            '& .MuiDataGrid-virtualScroller': {
              minWidth: 'max-content'
            }
          }}>
            <DataGrid
              rows={paginatedConsultations}
              columns={columns}
              hideFooter
              checkboxSelection
              onRowSelectionModelChange={setSelectedRows}
              rowSelectionModel={selectedRows}
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(error) => {
                console.error('Row update error:', error);
                setSnackbar({
                  open: true,
                  message: 'Failed to update row',
                  severity: 'error'
                });
              }}
              onCellClick={handleCellClick}
              onCellEditStart={handleCellEditStart}
              onCellEditStop={handleCellEditStop}
              isCellEditable={(params) => {
                const editableFields = ['quality', 'status', 'membership', 'followup', 'which_campaign'];
                return editableFields.includes(params.field);
              }}
              editMode="cell"
              loading={loading}
              autoHeight
              disableColumnMenu
              disableVirtualization={false}
              hideFooterPagination={true}
              scrollbarSize={0}
              sx={{
                minWidth: '1200px', // Ensure minimum width for all columns
                // Disable DataGrid's internal scrollbars to prevent double scrolling
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'visible !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  overflow: 'visible !important',
                },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                  overflow: 'visible !important',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell[data-field="phone"]': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                },
                '& .MuiDataGrid-cell[data-field="email"]': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                },
                '& .MuiDataGrid-row': {
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.001)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                },
                '& .MuiDataGrid-cell--editable': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    transition: 'all 0.2s ease-in-out',
                  }
                },
                '& .MuiDataGrid-editInputCell': {
                  backgroundColor: 'white !important',
                  border: '2px solid #1976d2 !important',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  '& input': {
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    outline: 'none !important',
                    fontSize: '14px',
                    padding: '0',
                    color: 'text.primary'
                  },
                  '& .MuiSelect-select': {
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    outline: 'none !important',
                    padding: '0',
                  }
                },
                '& .MuiDataGrid-editBooleanCell': {
                  backgroundColor: 'white !important',
                },
                // Remove the dark overlay completely
                '& .MuiDataGrid-overlay': {
                  backgroundColor: 'transparent !important',
                },
                '& .MuiDataGrid-overlayWrapper': {
                  backgroundColor: 'transparent !important',
                },
                // Custom styling for select dropdown in edit mode
                '& .MuiDataGrid-cell--editing': {
                  backgroundColor: 'white',
                  '& .MuiSelect-root': {
                    backgroundColor: 'white',
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: 'white',
                    border: 'none',
                  }
                }
              }}
            />
          </Paper>
        </Box>

        {/* Pagination */}
        {consultations.length > 0 && (
          <Box sx={{ px: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={totalConsultations}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ConsultationDashboard; 